import { Button } from '@getro/rombo';
import { ExternalLink } from 'lucide-react';
import React from 'react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { trackEvent } from 'helpers/analytics';
import { usePlaybooks } from './hooks/usePlaybooks';

export const Playbooks = ({ goals }) => {
  const playbooks = usePlaybooks(goals);

  return (
    <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
      <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
        <Text fontSize="16px" fontWeight="600" lineHeight="120%" color="text.dark">
          Start with a play from our playbook
        </Text>
        <Text fontSize="14px" fontWeight="400" lineHeight="120%" color="text.main">
          Easy, proven moves to grow through warm intros.
        </Text>
      </Flex>
      <Flex sx={{ flexDirection: 'row', gap: '24px', width: '100%', flexWrap: 'wrap' }}>
        {playbooks.map((playbook) => (
          <Box
            onClick={() => trackEvent('playbooks:see_playbook_click')}
            key={playbook.id}
            href={playbook.link}
            as="a"
            target="_blank"
            sx={{
              maxHeight: '108px',
              minWidth: '250px',
              position: 'relative',
              textDecoration: 'none',
              boxSizing: 'border-box',
              cursor: 'pointer',
              '&:hover': {
                '& > div': {
                  borderColor: 'purple.500',
                  transition: 'border-color 0.2s ease-in-out',
                },
                '#external-link-icon': {
                  opacity: '1',
                  transform: 'scale(1)',
                  transition: 'transform 0.2s ease-in-out',
                },
                [`#playbook-icon-${playbook.id}`]: {
                  transform: 'scale(1.3)',
                  transition: 'transform 0.2s ease-in-out',
                },
              },
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                gap: '8px',
                p: '16px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: '4px',
                maxWidth: '256px',
                width: '100%',
                textDecoration: 'none',
              }}
            >
              <Flex sx={{ justifyContent: 'space-between' }}>
                <Text fontSize="14px" fontWeight="500" lineHeight="120%" color="text.dark">
                  {playbook.title}
                </Text>
                <Box sx={{ cursor: 'pointer', width: '24px', height: '24px' }}>
                  <Box
                    id="external-link-icon"
                    as={ExternalLink}
                    size="16px"
                    sx={{
                      color: 'neutral.400',
                      opacity: '0',
                      transform: 'scale(0)',
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  />
                </Box>
              </Flex>
              <Text fontSize="14px" fontWeight="400" lineHeight="120%" color="text.main">
                {playbook.description}
              </Text>
            </Flex>

            <Image
              id={`playbook-icon-${playbook.id}`}
              src={playbook.icon}
              sx={{
                opacity: '0.15',
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                transform: 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </Box>
        ))}
      </Flex>
      <Flex sx={{ width: '100%', justifyContent: 'center' }}>
        <Button
          as="a"
          href="https://help.getro.com/support/solutions/folders/65000158491"
          target="_blank"
          size="medium"
          variant="accent"
          icon={ExternalLink}
          iconPosition="right"
          iconGap="8px"
          onClick={() => trackEvent('playbooks:see_all_playbooks_click')}
        >
          View full playbook
        </Button>
      </Flex>
    </Flex>
  );
};

Playbooks.propTypes = {
  goals: PropTypes.array.isRequired,
};
